import { useTranslation } from 'next-i18next'
import {
  ConfigurableProduct,
  useAddToCartMutation,
  useCartPageQuery,
  useFetchCartHeaderQuery,
} from '@magentoTypes'
import PulseLoader from 'react-spinners/PulseLoader'
import { useMedia } from '~/hooks/useMedia'
import { Image } from '@next/image'
import { Price } from '~/modules/PDP/components/Price'
import { useQueryClient } from '@tanstack/react-query'
import { createMagentoClient } from '~/graphql/magentoClient'
import { toast } from 'react-toastify'
import { getCartId, useCartId } from '~/hooks/useCartId'
import { Link } from '~/elements/Link/Link'
import { useProductAttributes } from '~/hooks/useProductAttributes'
import { DataLayerEnum, useGTM } from '~/hooks/useGTM'
import { useRouter } from 'next/router'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { CART_ID, DEFAULT_LOCALE } from '~/config/constants'
import { log } from '~/lib/log'
import { getSession } from 'next-auth/react'
import { ButtonMain } from '~/elements/Button/ButtonMain'
import { useMemo } from 'react'
import { getCioProductPrice, getCioVariationId } from '~/lib/cioHelpers'

type Props = {
  product: ConfigurableProduct
  variant?: number
}

export const CrossSellItem = ({ product, variant }: Props) => {
  const productVariant = variant ?? 0
  const isDesktop = useMedia('lg')
  const { t } = useTranslation()
  const { dataLayer } = useGTM()
  const router = useRouter()
  const { findLabel } = useProductAttributes()
  const queryClient = useQueryClient()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const mutationClient = createMagentoClient({ queryClient, usePost: true, locale: finalLocale })
  const [cartId] = useCartId()
  const productSku = product?.variants?.[productVariant]?.product?.sku
  const addToCart = useAddToCartMutation(mutationClient, {
    onSettled: async (data, error) => {
      if (data) {
        await queryClient.invalidateQueries(useCartPageQuery.getKey({ cartId }))
        await queryClient.invalidateQueries(useFetchCartHeaderQuery.getKey({ cartId }))
        toast.success(t('Added to cart'))
      } else {
        log(error)
        toast.error(error?.message)
      }
    },
    onError: async () => {
      await queryClient.invalidateQueries([CART_ID])
      await queryClient.invalidateQueries(['session'])
    },
  })

  const handleClick = async () => {
    if (!cartId && !productSku) return
    if (productSku) {
      const priceRange = product?.variants?.[productVariant]?.product?.price_range
      const price =
        priceRange?.minimum_price?.final_price?.value !==
        priceRange?.minimum_price?.regular_price?.value
          ? priceRange?.minimum_price?.final_price
          : priceRange?.minimum_price?.regular_price
      dataLayer(DataLayerEnum.EVENT, 'add_to_cart', {
        ecommerce: {
          currency: price?.currency || 'EUR',
          items: [
            {
              item_id: product?.variants?.[productVariant]?.product?.sku,
              item_ean: product?.variants?.[productVariant]?.product?.eannummer,
              item_name: product?.name,
              item_variant: product?.variants?.[productVariant]?.attributes?.[productVariant]
                ?.label as string,
              item_brand: findLabel({
                attributeCode: 'manufacturer',
                attributeValue: product?.manufacturer as number,
              }),
              item_category: '',
              price: price?.value,
              quantity: 1,
            },
          ],
        },
      })
      const session = await getSession()
      addToCart.mutate({
        cartId: session ? (cartId as string) : (getCartId() as string),
        cartItems: [
          {
            sku: product?.sku as string,
            quantity: 1,
            selected_options: [product?.variants?.[productVariant]?.attributes?.[0]?.uid as string],
          },
        ],
      })
    }
  }

  const cioVariationId = useMemo(() => {
    return getCioVariationId(product, productVariant)
  }, [product, productVariant])

  const cioProductPrice = useMemo(() => {
    return getCioProductPrice(product?.variants?.[productVariant]?.product?.price_range)
  }, [product, productVariant])

  return (
    <div
      className="grid w-full grid-cols-5 grid-rows-1 items-center justify-between gap-2"
      data-cnstrc-item-id={product?.sku}
      data-cnstrc-item-variation-id={cioVariationId}
      data-cnstrc-item-name={product?.name}
      data-cnstrc-item-price={cioProductPrice}
    >
      {product?.image?.url ? (
        <Link href={`${product?.url_key as string}.html`}>
          <Image
            src={product?.image?.url}
            alt={product?.image?.label ?? ''}
            width={isDesktop ? 64 : 48}
            height={isDesktop ? 64 : 48}
            hasNoBlur
          />
        </Link>
      ) : null}
      <div className="col-span-2 flex-auto">
        <Link href={`${product?.url_key as string}.html`}>
          <p className="mb-1 font-medium lg:mb-2">{product?.name}</p>
        </Link>
        <p className="font-extralight">
          {product?.variants?.[productVariant]?.attributes?.[0]?.label}
        </p>
      </div>
      <div className="flex flex-col items-end font-extralight lgx:flex-row lgx:items-center lgx:justify-end lgx:gap-2">
        <Price
          priceRange={product?.variants?.[productVariant]?.product?.price_range}
          regPriceClass="justify-self-end"
          salePriceClass="lgx:mr-0"
        />
      </div>
      <ButtonMain
        className="justify-self-end"
        variant="TextSmall"
        onClick={handleClick}
        data-cnstrc-btn="add_to_cart"
      >
        {addToCart.isLoading ? <PulseLoader loading={true} size={6} color={'#909090'} /> : t('add')}
      </ButtonMain>
    </div>
  )
}
